import {
  isOnboardingAttemptState,
  isOnboardingBootstrapActionCode,
  isOnboardingBootstrapFailureCode,
  isOnboardingBootstrapStage,
  isOnboardingPairingStatus,
  isPairingPurpose,
  type ResumeOnboardingAttemptResponse,
} from '@internal/spassu-code-contract';
import {
  object,
  hasOnlyKeys,
  validTimestamp,
  timestampOrNull,
  type JsonObject,
} from './controlPlaneDecoderValues';

export function attempt(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  const row = object(value);
  return Boolean(
    row &&
      attemptIdentity(row) &&
      attemptProgress(row) &&
      attemptDiagnostic(row.diagnostic),
  );
}

function attemptIdentity(row: JsonObject): boolean {
  return (
    hasOnlyKeys(row, [
      'sessionId',
      'purpose',
      'state',
      'stage',
      'stageChangedAt',
      'failedAt',
      'diagnostic',
    ]) &&
    typeof row.sessionId === 'string' &&
    isPairingPurpose(row.purpose) &&
    row.purpose !== 'cleanup-enrollment'
  );
}

function attemptProgress(row: JsonObject): boolean {
  return (
    isOnboardingAttemptState(row.state) &&
    isOnboardingBootstrapStage(row.stage) &&
    validTimestamp(row.stageChangedAt) &&
    timestampOrNull(row.failedAt)
  );
}

function attemptDiagnostic(value: unknown): boolean {
  if (value === null) return true;
  const diagnostic = object(value);
  return Boolean(
    diagnostic &&
      hasOnlyKeys(diagnostic, ['code', 'actionCode']) &&
      isOnboardingBootstrapFailureCode(diagnostic.code) &&
      isOnboardingBootstrapActionCode(diagnostic.actionCode),
  );
}

function resumeMetadata(row: JsonObject): boolean {
  return (
    hasOnlyKeys(row, [
      'id',
      'activityId',
      'status',
      'expiresAt',
      'createdAt',
      'codeAvailable',
      'pairingCode',
      'terminalCommand',
      'purpose',
    ]) &&
    typeof row.id === 'string' &&
    typeof row.activityId === 'string' &&
    isOnboardingPairingStatus(row.status) &&
    validTimestamp(row.expiresAt) &&
    validTimestamp(row.createdAt) &&
    typeof row.codeAvailable === 'boolean' &&
    (row.purpose === undefined || isPairingPurpose(row.purpose))
  );
}

function resumeMaterial(row: JsonObject): boolean {
  if (row.codeAvailable)
    return (
      typeof row.pairingCode === 'string' &&
      typeof row.terminalCommand === 'string'
    );
  return row.pairingCode === undefined && row.terminalCommand === undefined;
}

export function decodeResumeOnboardingAttemptResponse(
  value: unknown,
): ResumeOnboardingAttemptResponse {
  const row = object(value);
  if (!row || !resumeMetadata(row) || !resumeMaterial(row)) {
    throw new Error('A resposta de retomada da conexão é inválida.');
  }
  return value as ResumeOnboardingAttemptResponse;
}
